import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmt, ResultCard } from "./pipe-helpers";

/* ═══════════════════════════════════════════════════════
   PIPE THERMAL EXPANSION CALCULATOR
   ═══════════════════════════════════════════════════════ */

const PIPE_EXPANSION_COEFFICIENTS: { label: string; coeff: number }[] = [
  { label: "Carbon Steel", coeff: 6.33e-6 },
  { label: "Stainless Steel (304)", coeff: 9.6e-6 },
  { label: "Stainless Steel (316)", coeff: 8.9e-6 },
  { label: "Copper", coeff: 9.8e-6 },
  { label: "PVC", coeff: 30.0e-6 },
  { label: "CPVC", coeff: 34.7e-6 },
  { label: "Cast Iron", coeff: 5.9e-6 },
  { label: "Aluminum", coeff: 12.8e-6 },
];

export const PipeThermalExpansion = () => {
  const [matIdx, setMatIdx] = useState("0");
  const [length, setLength] = useState("");
  const [tempFrom, setTempFrom] = useState("70");
  const [tempTo, setTempTo] = useState("");

  const mat = PIPE_EXPANSION_COEFFICIENTS[parseInt(matIdx)];
  const L = parseFloat(length);
  const t1 = parseFloat(tempFrom);
  const t2 = parseFloat(tempTo);
  const hasInput = L > 0 && !isNaN(t1) && !isNaN(t2);
  const deltaT = t2 - t1;
  const expansionIn = hasInput ? mat.coeff * L * 12 * deltaT : 0;

  return (
    <div className="px-4 space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Calculate pipe <span className="text-foreground font-semibold">thermal expansion or contraction</span> based on material, length, and temperature change. ΔL = α × L × ΔT
        </p>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Pipe Material</label>
          <Select value={matIdx} onValueChange={setMatIdx}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PIPE_EXPANSION_COEFFICIENTS.map((m, i) => (
                <SelectItem key={i} value={String(i)}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Pipe Length (ft)</label>
          <Input type="number" inputMode="decimal" placeholder="Feet…" value={length} onChange={(e) => setLength(e.target.value)} className="font-mono-data bg-card border-border" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Install Temp (°F)</label>
            <Input type="number" inputMode="decimal" placeholder="70" value={tempFrom} onChange={(e) => setTempFrom(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Operating Temp (°F)</label>
            <Input type="number" inputMode="decimal" placeholder="°F…" value={tempTo} onChange={(e) => setTempTo(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
        </div>
      </div>
      {hasInput && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Total Movement" value={fmt(Math.abs(expansionIn), 3)} unit="in" accent />
          <ResultCard label="Direction" value={expansionIn >= 0 ? "Expansion ↑" : "Contraction ↓"} unit="" />
          <ResultCard label="Temp Change (ΔT)" value={fmt(deltaT)} unit="°F" />
          <ResultCard label="Coeff. (α)" value={(mat.coeff * 1e6).toFixed(1)} unit="×10⁻⁶ in/in/°F" />
          {Math.abs(expansionIn) > 1 && (
            <div className="bg-safety/10 border border-safety/30 rounded-xl p-3">
              <p className="text-xs text-safety font-semibold">⚡ Movement exceeds 1" — expansion loop or joint required.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   PIPE INSULATION THICKNESS (ASHRAE 90.1)
   ═══════════════════════════════════════════════════════ */

const INSULATION_TABLE: { tempRange: string; minF: number; maxF: number; sizes: Record<string, number> }[] = [
  { tempRange: "Chilled (40–55°F)", minF: 40, maxF: 55, sizes: { '< 1"': 0.5, '1"–1½"': 0.5, '2"–4"': 1.0, '5"–6"': 1.0, '8"+': 1.5 } },
  { tempRange: "Heating (105–140°F)", minF: 105, maxF: 140, sizes: { '< 1"': 1.0, '1"–1½"': 1.0, '2"–4"': 1.0, '5"–6"': 1.5, '8"+': 1.5 } },
  { tempRange: "Heating (141–200°F)", minF: 141, maxF: 200, sizes: { '< 1"': 1.5, '1"–1½"': 1.5, '2"–4"': 1.5, '5"–6"': 2.0, '8"+': 2.0 } },
  { tempRange: "HHW (201–250°F)", minF: 201, maxF: 250, sizes: { '< 1"': 1.5, '1"–1½"': 2.0, '2"–4"': 2.0, '5"–6"': 2.5, '8"+': 3.0 } },
  { tempRange: "Steam (251–350°F)", minF: 251, maxF: 350, sizes: { '< 1"': 2.0, '1"–1½"': 2.5, '2"–4"': 2.5, '5"–6"': 3.0, '8"+': 3.5 } },
  { tempRange: "Steam (>350°F)", minF: 351, maxF: 999, sizes: { '< 1"': 3.0, '1"–1½"': 3.0, '2"–4"': 3.5, '5"–6"': 3.5, '8"+': 4.0 } },
];

const PIPE_SIZE_CATEGORIES = ['< 1"', '1"–1½"', '2"–4"', '5"–6"', '8"+'];

export const PipeInsulationCalc = () => {
  const [fluidTemp, setFluidTemp] = useState("");
  const [pipeSizeCat, setPipeSizeCat] = useState("0");

  const tempVal = parseFloat(fluidTemp);
  const sizeKey = PIPE_SIZE_CATEGORIES[parseInt(pipeSizeCat)];

  const result = useMemo(() => {
    if (isNaN(tempVal)) return null;
    for (const row of INSULATION_TABLE) {
      if (tempVal >= row.minF && tempVal <= row.maxF) {
        return { range: row.tempRange, thickness: row.sizes[sizeKey] };
      }
    }
    return null;
  }, [tempVal, sizeKey]);

  return (
    <div className="px-4 space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Minimum <span className="text-foreground font-semibold">pipe insulation thickness</span> per ASHRAE 90.1 for mechanical piping. Actual requirements vary by climate zone.
        </p>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Fluid Temperature (°F)</label>
          <Input type="number" inputMode="decimal" placeholder="°F…" value={fluidTemp} onChange={(e) => setFluidTemp(e.target.value)} className="font-mono-data bg-card border-border" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Pipe Size Range</label>
          <Select value={pipeSizeCat} onValueChange={setPipeSizeCat}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PIPE_SIZE_CATEGORIES.map((s, i) => (
                <SelectItem key={i} value={String(i)}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {result && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Min. Insulation" value={String(result.thickness)} unit="in" accent />
          <ResultCard label="Temp Range" value={result.range} unit="" />
          <ResultCard label="Pipe Size" value={sizeKey} unit="" />
        </div>
      )}
      {tempVal > 0 && !result && tempVal < 40 && (
        <div className="bg-primary/10 border border-primary/30 rounded-xl p-3 animate-fade-in">
          <p className="text-xs text-primary font-semibold">ℹ️ Below 40°F — insulation with vapor barrier required. Consult ASHRAE for cold service.</p>
        </div>
      )}
    </div>
  );
};
